<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    src: {
        type: String,
        required: true
    },
    alt: {
        type: String,
        default: ''
    },
    placeholderColor: {
        type: String,
        default: '#374151' // gray-700
    },
    priority: {
        type: Boolean,
        default: false
    }
});

const imageRef = ref(null);
const isLoaded = ref(false);
const isInView = ref(props.priority); // Load immediately if priority
let observer = null;

function handleLoad() {
    isLoaded.value = true;
}

onMounted(() => {
    if (props.priority || !('IntersectionObserver' in window)) {
        isInView.value = true;
        return;
    }

    observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                isInView.value = true;
                observer.disconnect();
            }
        });
    }, {
        rootMargin: '50px'
    });

    if (imageRef.value) {
        observer.observe(imageRef.value);
    }
});

onUnmounted(() => {
    if (observer) {
        observer.disconnect();
    }
});
</script>

<template>
    <div ref="imageRef" class="lazy-image-container" :style="{ backgroundColor: placeholderColor }">
        <img
            v-if="isInView"
            :src="src"
            :alt="alt"
            :fetchpriority="priority ? 'high' : 'auto'"
            :class="['lazy-image', { 'loaded': isLoaded }]"
            @load="handleLoad"
        />
    </div>
</template>

<style scoped>
.lazy-image-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 0.375rem;
    border: 2px solid #6b7280;
}

.lazy-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
}

.lazy-image.loaded {
    opacity: 1;
}
</style>
